
/* eslint-disable react-refresh/only-export-components -- provider, types and hooks intentionally share this module */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ServiceType } from "./LeadContext";
import { phpApi } from "@/integrations/php/client";
import { toast } from "@/components/ui/use-toast";
import { calculateDistance } from "@/utils/geoUtils";
import {
  EMPTY_PARTNER_DIRECTORY_SUMMARY,
  fetchAdminPartnerDirectory,
  type AdminPartnerDirectorySummary,
  type AdminPartnerServiceCoverage,
  type PartnerAttentionReason,
  type PartnerMiniPageMissingField,
} from "@/api/adminPartners";
import { createAdminPartner, updateAdminPartner } from "@/api/adminPartner";

export interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  miniImageUrl?: string;
  miniDescription?: string;
  miniPageComplete?: boolean;
  miniPageMissing?: PartnerMiniPageMissingField[];
  services: ServiceType[];
  active: boolean;
  leadCount?: number;
  successCount?: number;
  latitude?: number;
  longitude?: number;
  radius?: number;
  /** From partners.created_at — needed by the "newest first" sort on /admin/partners. */
  createdAt?: string;
  leads30?: number;
  lastLead?: string | null;
  routingReady?: boolean;
  emailReady?: boolean;
  smsReady?: boolean;
  attentionReasons?: PartnerAttentionReason[];
  sms_opt_out: boolean; // Ensure this field is defined as required
}

interface PartnerContextType {
  partners: Partner[];
  loading: boolean;
  error: string | null;
  directorySummary: AdminPartnerDirectorySummary;
  serviceCoverage: AdminPartnerServiceCoverage[];
  generatedAt: string;
  addPartner: (partner: Omit<Partner, "id">) => Promise<void>;
  updatePartner: (id: string, data: Partial<Partner>) => Promise<void>;
  removePartner: (id: string) => Promise<void>;
  getPartnerById: (id: string) => Partner | undefined;
  findNearestPartners: (postalCode: string, service: ServiceType, latitude?: number, longitude?: number, locationText?: string) => Promise<Partner[]>;
  findFairestPartner: (service: ServiceType, latitude: number, longitude: number) => Promise<Partner | null>;
  refreshPartners: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [directorySummary, setDirectorySummary] = useState(EMPTY_PARTNER_DIRECTORY_SUMMARY);
  const [serviceCoverage, setServiceCoverage] = useState<AdminPartnerServiceCoverage[]>([]);
  const [generatedAt, setGeneratedAt] = useState("");

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const directory = await fetchAdminPartnerDirectory();
      setPartners(directory.partners.map((partner) => ({
        id: partner.id,
        name: partner.name,
        phone: partner.phone,
        email: partner.email,
        location: partner.location,
        miniImageUrl: partner.mini_image_url || undefined,
        miniDescription: partner.mini_description || undefined,
        miniPageComplete: partner.mini_page_complete,
        miniPageMissing: partner.mini_page_missing,
        services: partner.services,
        active: partner.active,
        latitude: partner.latitude ?? undefined,
        longitude: partner.longitude ?? undefined,
        radius: partner.radius ?? undefined,
        createdAt: partner.created_at || undefined,
        leadCount: partner.total_leads,
        leads30: partner.leads_30d,
        successCount: partner.success_leads,
        lastLead: partner.last_lead,
        routingReady: partner.routing_ready,
        emailReady: partner.email_ready,
        smsReady: partner.sms_ready,
        attentionReasons: partner.attention_reasons,
        sms_opt_out: partner.sms_opt_out,
      })));
      setDirectorySummary(directory.summary);
      setServiceCoverage(directory.service_coverage);
      setGeneratedAt(directory.generated_at);
    } catch (caughtError) {
      console.error("Error fetching partners:", caughtError);
      const message = caughtError instanceof Error ? caughtError.message : "Δεν ήταν δυνατή η φόρτωση των συνεργατών.";
      setError(message);
      toast({
        title: "Σφάλμα",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPartners();
  }, [fetchPartners]);

  const refreshPartners = async () => {
    await fetchPartners();
  };

  const addPartner = async (partnerData: Omit<Partner, "id">) => {
    try {
      await createAdminPartner({
        name: partnerData.name.trim(),
        phone: partnerData.phone.trim(),
        email: partnerData.email.trim(),
        location: partnerData.location.trim(),
        miniImageUrl: partnerData.miniImageUrl?.trim() || null,
        miniDescription: partnerData.miniDescription?.trim() || null,
        services: partnerData.services,
        active: partnerData.active,
        latitude: partnerData.latitude ?? null,
        longitude: partnerData.longitude ?? null,
        radius: partnerData.radius || 10,
        smsOptOut: partnerData.sms_opt_out ?? false,
      });
      await fetchPartners();
    } catch (error) {
      console.error("Error adding partner:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η προσθήκη νέου συνεργάτη.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updatePartner = async (id: string, data: Partial<Partner>) => {
    try {
      const current = partners.find((partner) => partner.id === id);
      if (!current) throw new Error("Ο συνεργάτης δεν βρέθηκε.");
      await updateAdminPartner(id, {
        name: data.name ?? current.name,
        phone: data.phone ?? current.phone,
        email: data.email ?? current.email,
        location: data.location ?? current.location,
        miniImageUrl: data.miniImageUrl === undefined ? current.miniImageUrl ?? null : data.miniImageUrl || null,
        miniDescription: data.miniDescription === undefined ? current.miniDescription ?? null : data.miniDescription || null,
        services: data.services ?? current.services,
        active: data.active ?? current.active,
        smsOptOut: data.sms_opt_out ?? current.sms_opt_out,
        latitude: data.latitude === undefined ? current.latitude ?? null : data.latitude,
        longitude: data.longitude === undefined ? current.longitude ?? null : data.longitude,
        radius: data.radius ?? current.radius ?? 10,
      });

      await fetchPartners();
    } catch (error) {
      console.error("Error updating partner:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η ενημέρωση του συνεργάτη.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const removePartner = async (id: string) => {
    try {
      const { data: leadsData, error: leadsError } = await phpApi
        .from('leads')
        .select('id')
        .eq('partner_id', id)
        .limit(1);

      if (leadsError) throw leadsError;

      if (leadsData && leadsData.length > 0) {
        throw new Error("Δεν είναι δυνατή η διαγραφή του συνεργάτη επειδή έχει συνδεδεμένες επαφές (leads). Απενεργοποιήστε τον συνεργάτη αντί να τον διαγράψετε.");
      }

      const { error } = await phpApi
        .from('partners')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchPartners();
    } catch (error) {
      console.error("Error removing partner:", error);
      
      if (error instanceof Error) {
        toast({
          title: "Σφάλμα",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Σφάλμα",
          description: "Δεν ήταν δυνατή η διαγραφή του συνεργάτη.",
          variant: "destructive",
        });
      }
      
      throw error;
    }
  };

  const getPartnerById = (id: string) => {
    return partners.find(partner => partner.id === id);
  };

  // Function to find partners based on text location (city/region name)
  const findPartnersByLocationText = (
    locationText: string,
    eligiblePartners: Partner[]
  ): Partner[] => {
    if (!locationText || locationText.trim().length < 3) {
      return [];
    }

    const normalizedText = locationText.trim().toLowerCase();
    
    // First try to find exact matches in partner locations
    const exactMatches = eligiblePartners.filter(partner => {
      const partnerLocation = partner.location.toLowerCase();
      return partnerLocation.includes(normalizedText);
    });

    if (exactMatches.length > 0) {
      console.log(`Found ${exactMatches.length} partners matching location text: ${locationText}`);
      return exactMatches;
    }

    // Try fuzzy matching by checking if any part of the partner's location contains
    // at least 3 characters of the location text or vice versa
    const fuzzyMatches = eligiblePartners.filter(partner => {
      const partnerLocation = partner.location.toLowerCase();
      
      // Check if any 3+ character substring is shared
      for (let i = 0; i <= normalizedText.length - 3; i++) {
        const substring = normalizedText.substring(i, i + 3);
        if (partnerLocation.includes(substring)) {
          return true;
        }
      }
      
      // Check the reverse - if partner location contains parts of the input
      for (let i = 0; i <= partnerLocation.length - 3; i++) {
        const substring = partnerLocation.substring(i, i + 3);
        if (normalizedText.includes(substring)) {
          return true;
        }
      }
      
      return false;
    });

    console.log(`Found ${fuzzyMatches.length} partners with fuzzy location match for: ${locationText}`);
    return fuzzyMatches;
  };

  const findNearestPartners = async (
    postalCode: string, 
    service: ServiceType,
    latitude?: number,
    longitude?: number,
    locationText?: string
  ): Promise<Partner[]> => {
    const activePartners = partners.filter(
      partner => partner.active && partner.services.includes(service)
    );

    if (activePartners.length === 0) {
      console.log("No active partners found offering this service");
      return [];
    }

    // Case 1: We have coordinates - use direct distance calculation
    if (latitude && longitude) {
      console.log(`Finding partners for coordinates: Lat ${latitude}, Lng ${longitude}`);
      
      const nearbyPartners = activePartners.filter(partner => {
        if (!partner.latitude || !partner.longitude) return false;
        
        const distance = calculateDistance(latitude, longitude, partner.latitude, partner.longitude);
        const isInRange = distance <= (partner.radius || 10);
        
        if (isInRange) {
          console.log(`Partner ${partner.name} is in range (${distance.toFixed(2)}km, radius: ${partner.radius}km)`);
        }
        
        return isInRange;
      });
      
      // Sort partners by distance
      if (nearbyPartners.length > 0) {
        return nearbyPartners.sort((a, b) => {
          if (!a.latitude || !a.longitude || !b.latitude || !b.longitude) return 0;
          
          const distanceA = calculateDistance(latitude, longitude, a.latitude, a.longitude);
          const distanceB = calculateDistance(latitude, longitude, b.latitude, b.longitude);
          
          return distanceA - distanceB;
        });
      }
    }
    
    // Case 2: We have location text (city, region, etc.)
    if (locationText && locationText.trim().length > 2) {
      console.log(`Finding partners by location text: ${locationText}`);
      
      const matchedPartners = findPartnersByLocationText(locationText, activePartners);
      if (matchedPartners.length > 0) {
        // Sort by lead count for fair distribution
        return [...matchedPartners].sort((a, b) => {
          const countA = a.leadCount || 0;
          const countB = b.leadCount || 0;
          return countA - countB;
        });
      }
    }
    
    // Case 3: We have postal code but no coordinates
    if (postalCode && postalCode.trim().length > 0) {
      console.log(`Finding partners for postal code: ${postalCode}`);
      
      // If partners have location data, try to geocode the postal code
      const partnersWithLocation = activePartners.filter(
        p => p.latitude && p.longitude
      );
      
      if (partnersWithLocation.length > 0 && window.google && window.google.maps) {
        try {
          // Attempt to geocode the postal code
          const geocoder = new window.google.maps.Geocoder();
          
          const geocodeResult = await new Promise<google.maps.GeocoderResult | null>((resolve, reject) => {
            geocoder.geocode(
              { 
                address: postalCode, 
                componentRestrictions: { country: "gr" } 
              },
              (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
                  resolve(results[0]);
                } else {
                  console.warn(`Geocoding postal code failed: ${status}`);
                  resolve(null);
                }
              }
            );
          });
          
          if (geocodeResult) {
            const location = geocodeResult.geometry.location;
            const lat = location.lat();
            const lng = location.lng();
            
            console.log(`Geocoded postal code ${postalCode} to coordinates: Lat ${lat}, Lng ${lng}`);
            
            // Extract locality/city information for text matching if needed
            let locality = "";
            let region = "";
            
            if (geocodeResult.address_components) {
              for (const component of geocodeResult.address_components) {
                if (component.types.includes("locality")) {
                  locality = component.long_name;
                }
                if (component.types.includes("administrative_area_level_1")) {
                  region = component.long_name;
                }
              }
            }
            
            // Now we have coordinates, filter partners by distance
            const nearbyPartners = partnersWithLocation.filter(partner => {
              const distance = calculateDistance(lat, lng, partner.latitude!, partner.longitude!);
              return distance <= (partner.radius || 10);
            });
            
            // Sort partners by distance
            if (nearbyPartners.length > 0) {
              return nearbyPartners.sort((a, b) => {
                const distanceA = calculateDistance(lat, lng, a.latitude!, a.longitude!);
                const distanceB = calculateDistance(lat, lng, b.latitude!, b.longitude!);
                
                return distanceA - distanceB;
              });
            }
            
            // If no partners found by distance, try to match by locality/region name 
            if (locality || region) {
              const locationText = [locality, region].filter(Boolean).join(", ");
              console.log(`No partners in range by distance, trying text matching with: ${locationText}`);
              
              const textMatches = findPartnersByLocationText(locationText, activePartners);
              if (textMatches.length > 0) {
                return textMatches;
              }
            }
          }
        } catch (error) {
          console.error("Error geocoding postal code:", error);
        }
      }
      
      // Try to match postal code prefix (first 2-3 digits) directly with partner postal codes
      // This works if partners have postal code information embedded in their location field
      if (postalCode.length >= 2) {
        const postalPrefix = postalCode.substring(0, 3);
        console.log(`Trying to match postal code prefix: ${postalPrefix}`);
        
        const postalMatches = activePartners.filter(partner => {
          // Extract potential postal code from partner location
          const postalMatch = partner.location.match(/\b(\d{5})\b/);
          if (postalMatch) {
            const partnerPostal = postalMatch[1];
            return partnerPostal.startsWith(postalPrefix);
          }
          return false;
        });
        
        if (postalMatches.length > 0) {
          console.log(`Found ${postalMatches.length} partners with matching postal code prefix`);
          return postalMatches;
        }
      }
      
      // Last resort: return all active partners with fewer leads
      console.log("Falling back to all active partners sorted by lead count");
    }
    
    // Case 4: No location data at all - return all active partners
    console.log("No location data, returning all active partners sorted by lead count");
    return [...activePartners].sort((a, b) => {
      const countA = a.leadCount || 0;
      const countB = b.leadCount || 0;
      return countA - countB;
    });
  };

  const findFairestPartner = async (
    service: ServiceType,
    latitude: number,
    longitude: number
  ): Promise<Partner | null> => {
    if (!latitude || !longitude) return null;

    const eligiblePartners = partners.filter(partner => 
      partner.active && 
      partner.services.includes(service)
    );

    if (eligiblePartners.length === 0) return null;

    if (eligiblePartners.length === 1) return eligiblePartners[0];

    const partnersInRange = eligiblePartners.filter(partner => {
      if (!partner.latitude || !partner.longitude || !partner.radius) return false;
      
      const distance = calculateDistance(latitude, longitude, partner.latitude, partner.longitude);
      return distance <= partner.radius;
    });

    if (partnersInRange.length === 0) return null;
    if (partnersInRange.length === 1) return partnersInRange[0];

    const partnersWithScores = partnersInRange.map(partner => {
      const distance = calculateDistance(
        latitude, 
        longitude, 
        partner.latitude!, 
        partner.longitude!
      );
      
      const distanceScore = 1 - (distance / (partner.radius || 10));
      
      const leadCount = partner.leadCount || 0;
      const maxLeads = Math.max(...partnersInRange.map(p => p.leadCount || 0), 1);
      const leadCountScore = 1 - (leadCount / maxLeads);
      
      const successCount = partner.successCount || 0;
      const successRate = leadCount > 0 ? successCount / leadCount : 0;
      
      const score = (leadCountScore * 0.5) + (distanceScore * 0.3) + (successRate * 0.2);
      
      return {
        ...partner,
        score
      };
    });

    partnersWithScores.sort((a, b) => (b.score || 0) - (a.score || 0));
    return partnersWithScores[0];
  };

  return (
    <PartnerContext.Provider
      value={{
        partners,
        loading,
        error,
        directorySummary,
        serviceCoverage,
        generatedAt,
        addPartner,
        updatePartner,
        removePartner,
        getPartnerById,
        findNearestPartners,
        findFairestPartner,
        refreshPartners,
      }}
    >
      {children}
    </PartnerContext.Provider>
  );
};

export const usePartners = () => {
  const context = useContext(PartnerContext);
  if (context === undefined) {
    throw new Error("usePartners must be used within a PartnerProvider");
  }
  return context;
};

export const useOptionalPartners = () => useContext(PartnerContext);
